import { URNS } from "./HallDecor";
import { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { BENCH_Z, BOOK, BOOK_STAND, ROOM, SIGN } from "../../lib/layout";
import { BENCH_W, corrX, FEEL_STAND, feelFront, frontOf, glidePath, MEMORY_STAND, regionAt, roomEntry, walkable, windowsStand, worldLayout } from "../../lib/world";
import { SECTION_IDS } from "../../lib/sections";
import { clamp } from "../../lib/color";
import { mark, perf, times } from "../../lib/perf";
import { cine } from "./SeatCamera";

export const pose = { x: FEEL_STAND.x, z: FEEL_STAND.z, yaw: 0, pitch: -0.03 };

const SPEED = 3.8;
const MARGIN = 0.7;
const BOXES = [{ x: 0, z: BENCH_Z, hx: 1.3 + 0.4, hz: 0.38 + 0.4 }];
export { SIGN };
export const solids = { bench: false };
const SIGN_ONLY = [{ x: SIGN.x, z: SIGN.z, hx: 0.5 + 0.4, hz: 0.45 + 0.4 }, { x: BOOK.x, z: BOOK.z, hx: 0.42 + 0.4, hz: 0.42 + 0.4 }, { x: BENCH_W.x, z: BENCH_W.z, hx: 1.2 + 0.4, hz: 0.28 + 0.4 }, ...URNS.map((u) => ({ x: u.x, z: u.z, hx: 0.35 + 0.4, hz: 0.35 + 0.4 }))];
const ALL_BOXES = [...SIGN_ONLY, ...BOXES];
const KEYMAP = {
  KeyW: "f", ArrowUp: "f", KeyS: "b", ArrowDown: "b",
  KeyA: "l", ArrowLeft: "l", KeyD: "r", ArrowRight: "r",
};

export const glide = { on: false };
export const portal = { on: false };
const here = () => ({ x: pose.x, z: pose.z });
let queued = null;
// a pick made while a glide or portal is still running waits its turn instead of vanishing
export function flushQueued() {
  const t = queued;
  queued = null;
  if (t) glideTo(t);
}
export function glideTo(target) {
  if (glide.on || portal.on) {
    queued = target;
    return;
  }
  const cur = regionAt(pose.x, pose.z);
  const room = SECTION_IDS.includes(target);
  if (cur === "hall" && !room) {
    const { pts, yaw } = glidePath(pose, target);
    return startGlide(pts, yaw, target);
  }
  if (cur === "hall") return startGlide([here(), feelFront(target)], 0, target, () => portalTo(target));
  if (cur === target) {
    // already in this room: go back to the painting being shown, never to a blank wall
    const s = window.__afaeStepSlot?.();
    if (s) return glideToSlot(s);
    const e = roomEntry(target);
    return startGlide([here(), { x: e.x, z: e.z }], e.yaw, target);
  }
  portalTo(target);
}
export function portalTo(target) {
  if (portal.on) return;
  portal.on = true;
  portal.target = target;
  window.dispatchEvent(new CustomEvent("afae-portal", { detail: target }));
}
export function arrive(target) {
  const first = SECTION_IDS.includes(target) && window.__afaeFirst?.(target);
  if (first) focus.slot = first;
  else focus.slot = null;
  const d = target === "hall" ? FEEL_STAND : target === "memory" ? MEMORY_STAND : target === "windows" ? windowsStand() : target === "flipbook" ? BOOK_STAND : first ? frontOf(first, fitK()) : roomEntry(target);
  Object.assign(pose, { x: d.x, z: d.z, yaw: d.yaw, pitch: -0.02 });
  if (SECTION_IDS.includes(target)) sessionStorage.setItem("afae-chosen", "1");
}
export const STAND = 2.2;
export const focus = { slot: null };
export const lights = { all: false };
export function skipGlide() {
  if (glide.on) glide.start = performance.now() - glide.dur * 1000;
}
const fitK = () => {
  const aspect = window.innerWidth / Math.max(1, window.innerHeight);
  const half = Math.tan((33 * Math.PI) / 180) * aspect;
  return Math.min(3.4, Math.max(STAND, 1.0 / half, 1.05 / Math.tan((33 * Math.PI) / 180)));
};
// glide to stand ~2.2m in front of a hung painting, from anywhere in the gallery
export function glideToSlot(s) {
  if (regionAt(pose.x, pose.z) !== s.section) return portalTo(s.section);
  focus.slot = s;
  const f = frontOf(s, fitK());
  const pts = [here()];
  if (s.section === "thoughts") {
    const last = pts[pts.length - 1];
    const dir = Math.sign(f.z - last.z);
    for (let z = last.z + dir * 1.5; dir * (f.z - z) > 1; z += dir * 1.5) pts.push({ x: corrX(z), z });
  }
  pts.push({ x: f.x, z: f.z });
  startGlide(pts, f.yaw, s.section);
}
export function glideOverview(id, done, dur = 5) {
  focus.slot = null;
  const e = roomEntry(id);
  startGlide([here(), { x: e.x, z: e.z }], e.yaw, id, done, dur);
}
function startGlide(pts, yaw, target, done = null, fixed = 0) {
  const segs = [];
  let len = 0;
  for (let i = 1; i < pts.length; i++) {
    const l = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].z - pts[i - 1].z);
    segs.push({ a: pts[i - 1], b: pts[i], s: len, l });
    len += l;
  }
  Object.assign(glide, { on: true, first: false, t: 0, start: performance.now(), segs, len, yaw, dur: fixed || Math.min(5, Math.max(2, 1.4 + len / 7)), target, done });
}
const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const turn = (from, to, k) => from + Math.atan2(Math.sin(to - from), Math.cos(to - from)) * k;

function stepGlide(dt) {
  const g = glide;
  if (!g.first) {
    g.first = true;
    if (times.nextTap && !times.glideFirst) mark("glideFirst");
  }
  g.t = Math.min(1, (performance.now() - g.start) / 1000 / g.dur);
  const d = ease(g.t) * g.len;
  const s = g.segs.find((q) => d <= q.s + q.l) || g.segs[g.segs.length - 1];
  if (s) {
    const f = s.l ? (d - s.s) / s.l : 1;
    pose.x = s.a.x + (s.b.x - s.a.x) * f;
    pose.z = s.a.z + (s.b.z - s.a.z) * f;
    const dirYaw = Math.atan2(-(s.b.x - s.a.x), -(s.b.z - s.a.z));
    pose.yaw = turn(pose.yaw, g.t > 0.8 || !s.l ? g.yaw : dirYaw, Math.min(1, 1 - Math.exp(-5 * dt) + (g.t > 0.8 ? (g.t - 0.8) * 2 : 0)));
  }
  pose.pitch += (-0.02 - pose.pitch) * (1 - Math.exp(-4 * dt));
  if (g.t >= 1) {
    pose.yaw = g.yaw;
    g.on = false;
    if (times.glideFirst && !times.glideEnd) mark("glideEnd");
    const cb = g.done;
    g.done = null;
    if (cb) cb();
    else flushQueued();
  }
}

export function standBefore(paintings, slug) {
  const p = paintings.find((q) => q.slug === slug);
  const s = p && worldLayout(paintings).slots[p.id];
  if (!s) return false;
  Object.assign(pose, frontOf(s, 3), { pitch: -0.02 });
  return true;
}

function collide(p, ox, oz) {
  if (!walkable(p.x, p.z)) {
    if (walkable(p.x, oz)) p.z = oz;
    else if (walkable(ox, p.z)) p.x = ox;
    else (p.x = ox), (p.z = oz);
  }
  for (const b of solids.bench ? ALL_BOXES : SIGN_ONLY) {
    const dx = p.x - b.x;
    const dz = p.z - b.z;
    const px = b.hx - Math.abs(dx);
    const pz = b.hz - Math.abs(dz);
    if (px > 0 && pz > 0) {
      if (px < pz) p.x += Math.sign(dx || 1) * px;
      else p.z += Math.sign(dz || 1) * pz;
    }
  }
}

function useKeys(enabledRef, onActivate) {
  const keys = useRef({});
  useEffect(() => {
    const down = (e) => {
      if (!enabledRef.current) return;
      const k = KEYMAP[e.code];
      if (k) {
        keys.current[k] = true;
        e.preventDefault();
      }
      if (e.code === "ShiftLeft" || e.code === "ShiftRight") keys.current.run = true;
      if (e.code === "Enter" || e.code === "KeyE") onActivate();
    };
    const up = (e) => {
      const k = KEYMAP[e.code];
      if (k) keys.current[k] = false;
      if (e.code.startsWith("Shift")) keys.current.run = false;
    };
    const reset = () => (keys.current = {});
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    window.addEventListener("blur", reset);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
      window.removeEventListener("blur", reset);
    };
  }, [enabledRef, onActivate]);
  return keys;
}

function useDragLook(gl, enabledRef) {
  useEffect(() => {
    const el = gl.domElement;
    let drag = null;
    const pd = (e) => {
      if (enabledRef.current) drag = { x: e.clientX, y: e.clientY, id: e.pointerId };
    };
    const pm = (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const s = e.pointerType === "touch" ? 0.0055 : 0.0032;
      pose.yaw -= (e.clientX - drag.x) * s;
      pose.pitch = clamp(pose.pitch - (e.clientY - drag.y) * s, -1.0, 1.0);
      drag.x = e.clientX;
      drag.y = e.clientY;
    };
    const pu = (e) => {
      if (drag && e.pointerId === drag.id) drag = null;
    };
    el.addEventListener("pointerdown", pd);
    window.addEventListener("pointermove", pm);
    window.addEventListener("pointerup", pu);
    window.addEventListener("pointercancel", pu);
    return () => {
      el.removeEventListener("pointerdown", pd);
      window.removeEventListener("pointermove", pm);
      window.removeEventListener("pointerup", pu);
      window.removeEventListener("pointercancel", pu);
    };
  }, [gl, enabledRef]);
}

export function Player({ enabled, moveRef, meshesRef, onAim, onActivate }) {
  const { camera, gl } = useThree();
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const aimRef = useRef(null);
  const vel = useMemo(() => new THREE.Vector3(), []);
  const ray = useMemo(() => new THREE.Raycaster(undefined, undefined, 0.1, 9), []);
  const center = useMemo(() => new THREE.Vector2(0, 0), []);
  const activate = useMemo(() => () => aimRef.current && onActivate(aimRef.current), [onActivate]);
  const keys = useKeys(enabledRef, activate);
  useDragLook(gl, enabledRef);

  useEffect(() => {
    camera.rotation.order = "YXZ";
  }, [camera]);
  useEffect(() => {
    if (enabled) aimRef.current = undefined;
  }, [enabled]);

  useFrame((state, delta) => {
    const dt = Math.min(delta, 0.05);
    if (delta > 0) perf.fps += (Math.min(120, 1 / delta) - perf.fps) * 0.03;
    if (times.nextTap && !times.frame1) mark("frame1");
    if (cine.mode) return;
    const k = keys.current;
    const m = moveRef.current;
    let fwd = (k.f ? 1 : 0) - (k.b ? 1 : 0) - m.y;
    let str = (k.r ? 1 : 0) - (k.l ? 1 : 0) + m.x;
    const len = Math.hypot(fwd, str);
    if (len > 1) {
      fwd /= len;
      str /= len;
    }
    const sp = SPEED * (k.run ? 1.6 : 1);
    const sy = Math.sin(pose.yaw);
    const cy = Math.cos(pose.yaw);
    const tx = (-sy * fwd + cy * str) * sp;
    const tz = (-cy * fwd - sy * str) * sp;
    const a = 1 - Math.exp(-9 * dt);
    vel.x += (tx - vel.x) * a;
    vel.z += (tz - vel.z) * a;
    const ox = pose.x;
    const oz = pose.z;
    if (glide.on) {
      stepGlide(dt);
      vel.x = vel.z = 0;
    } else if (portal.on) {
      vel.x = vel.z = 0;
    } else {
      pose.x += vel.x * dt;
      pose.z += vel.z * dt;
      collide(pose, ox, oz);
    }
    const moving = Math.hypot(vel.x, vel.z) / SPEED;
    const want = focus.slot && camera.aspect < 1 ? Math.min(92, Math.max(66, (2 * Math.atan(0.98 / Math.max(1, Math.hypot(pose.x - focus.slot.x, pose.z - focus.slot.z)) / camera.aspect) * 180) / Math.PI)) : 66;
    if (Math.abs(camera.fov - want) > 0.1) {
      camera.fov += (want - camera.fov) * Math.min(1, dt * 3);
      camera.updateProjectionMatrix();
    }
    camera.position.set(pose.x, ROOM.EYE + Math.sin(state.clock.elapsedTime * 7) * 0.018 * moving, pose.z);
    camera.rotation.set(pose.pitch, pose.yaw, 0);

    ray.setFromCamera(center, camera);
    const reg = regionAt(pose.x, pose.z);
    const hit = ray.intersectObjects(Object.values(meshesRef.current), false).find((h) => h.object.userData.zone === reg);
    const id = hit?.object.userData.paintingId || null;
    if (id !== aimRef.current) {
      aimRef.current = id;
      onAim(id);
    }
  });
  return null;
}
